import { Link } from 'react-router-dom';
import { useState, FormEvent } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d5j6h46ihetfqh7m5lj0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#1a2332] mx-3 md:mx-5 mb-3 md:mb-5 rounded-t-2xl md:rounded-t-3xl">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-20 py-10 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 lg:gap-16 mb-10 md:mb-16">
          {/* Brand Column */}
          <div>
            <img
              src="https://static.readdy.ai/image/c547be34c1bf7bb39243860cf03ecf7c/44a2401a64e2a2728e9bc538c6daa347.png"
              alt="UKPropertiez"
              className="h-10 md:h-12 mb-4 md:mb-6"
            />
            <p className="text-sm md:text-base text-[#96999c] leading-relaxed">
              Providing reliable property management and housing solutions across the UK.
            </p>
          </div>

          {/* Quick Links Column */}
          <nav>
            <h3 className="text-base md:text-lg font-bold text-white mb-4 md:mb-6">Quick Links</h3>
            <ul className="space-y-3 md:space-y-4">
              <li>
                <Link to="/" className="text-sm md:text-base text-[#96999c] hover:text-[#00b2f6] transition-colors cursor-pointer">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm md:text-base text-[#96999c] hover:text-[#00b2f6] transition-colors cursor-pointer">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-sm md:text-base text-[#96999c] hover:text-[#00b2f6] transition-colors cursor-pointer">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/approach" className="text-sm md:text-base text-[#96999c] hover:text-[#00b2f6] transition-colors cursor-pointer">
                  Our Approach
                </Link>
              </li>
              <li>
                <Link to="/partners" className="text-sm md:text-base text-[#96999c] hover:text-[#00b2f6] transition-colors cursor-pointer">
                  Who We Work With
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm md:text-base text-[#96999c] hover:text-[#00b2f6] transition-colors cursor-pointer">
                  Contact
                </Link>
              </li>
            </ul>
          </nav>

          {/* Services Column */}
          <div>
            <h3 className="text-base md:text-lg font-bold text-white mb-4 md:mb-6">Services</h3>
            <ul className="space-y-3 md:space-y-4">
              <li className="text-sm md:text-base text-[#96999c]">Residential Property Management</li>
              <li className="text-sm md:text-base text-[#96999c]">Social Housing</li>
              <li className="text-sm md:text-base text-[#96999c]">Student Accommodation</li>
              <li className="text-sm md:text-base text-[#96999c]">Serviced Accommodation</li>
              <li className="text-sm md:text-base text-[#96999c]">Mixed-Use Properties</li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-base md:text-lg font-bold text-white mb-4 md:mb-6">Get in Touch</h3>
            <ul className="space-y-3 md:space-y-4">
              <li>
                <a href="mailto:info@ukpropertiez.co.uk" className="text-sm md:text-base text-[#96999c] hover:text-[#00b2f6] transition-colors cursor-pointer">
                  info@ukpropertiez.co.uk
                </a>
              </li>
              <li>
                <a href="tel:+443332100143" className="text-sm md:text-base text-[#96999c] hover:text-[#00b2f6] transition-colors cursor-pointer">
                  +44 (0) 333 210 0143
                </a>
              </li>
            </ul>
            <div className="mt-6 md:mt-8">
              <p className="text-xs md:text-sm text-[#96999c] mb-3">Subscribe to updates</p>
              <form id="newsletter-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-2">
                <div className="flex">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    disabled={isSubmitting}
                    className="flex-1 h-10 px-4 bg-white/10 border border-white/20 rounded-l-full text-xs md:text-sm text-white placeholder-[#96999c] focus:outline-none focus:border-[#00b2f6] disabled:opacity-50"
                  />
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-10 h-10 bg-[#00b2f6] rounded-r-full flex items-center justify-center hover:bg-[#0099d6] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <i className="ri-loader-4-line text-white animate-spin"></i>
                    ) : (
                      <i className="ri-arrow-right-line text-white"></i>
                    )}
                  </button>
                </div>
                {submitStatus === 'success' && (
                  <p className="text-xs text-green-400">Thanks for subscribing!</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-xs text-red-400">Please enter a valid email</p>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 md:pt-10 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0">
          <p className="text-xs md:text-sm text-[#96999c] text-center md:text-left">
            © {new Date().getFullYear()} UKPropertiez Group Limited. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
            <span className="text-xs md:text-sm text-[#96999c] cursor-pointer hover:text-[#00b2f6] transition-colors">Privacy Policy</span>
            <span className="text-xs md:text-sm text-[#96999c] cursor-pointer hover:text-[#00b2f6] transition-colors">Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
